// ============================================================
// Layout Super Admin — Lot L04
// ============================================================
import React, { useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import { Building2, Tag, LogOut, Archive, ScrollText, Activity, ShieldCheck } from 'lucide-react';
import FooterMentions from '../../components/common/FooterMentions';
import { resolveUploadUrl, initiales } from '../../utils/media';
import { resetFavicon } from '../../utils/favicon';

export default function SuperAdminLayout() {
  const { superAdmin, logout } = useSuperAdminAuth();
  const navigate = useNavigate();

  // Marquer le contexte SA : titre d'onglet + reset thème CSS + favicon neutre
  // (sinon le favicon d'un commerce mis en cache resterait affiché ici).
  useEffect(() => {
    document.title = 'Console Super Admin';
    resetFavicon();
    const root = document.documentElement;
    root.style.setProperty('--sv-accent', '#2563eb');
    root.style.setProperty('--sv-accent-soft', 'rgba(37, 99, 235, 0.10)');
    root.style.setProperty('--sv-accent-border', 'rgba(37, 99, 235, 0.25)');
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/superadmin/login');
  };

  return (
    <div className="sa-scope min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => window.location.reload()}
            title="Actualiser la page"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer text-left"
          >
            <img
              src="/logos-gc/app-icon-64.png"
              alt="Gestion Commerce"
              className="w-9 h-9 rounded-lg border border-blue-200 object-contain bg-white"
            />
            <div>
              <h1 className="text-sm font-semibold">Console Super Admin</h1>
              <p className="text-xs text-slate-400">Plateforme de Gestion des Commerces</p>
            </div>
          </button>

          <nav className="hidden sm:flex items-center gap-1">
            <NavLink
              to="/superadmin/commerces"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <Building2 size={14} /> Commerces
            </NavLink>
            <NavLink
              to="/superadmin/presence"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <Activity size={14} /> Présence
            </NavLink>
            <NavLink
              to="/superadmin/plans"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <Tag size={14} /> Plans
            </NavLink>
            <NavLink
              to="/superadmin/sauvegardes"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <Archive size={14} /> Sauvegardes
            </NavLink>
            <NavLink
              to="/superadmin/audit-logs"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <ScrollText size={14} /> Audit
            </NavLink>
            <NavLink
              to="/superadmin/super-admins"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-100'
                }`
              }
            >
              <ShieldCheck size={14} /> Super Admins
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            {superAdmin && (
              <NavLink
                to="/superadmin/profil"
                title="Mon profil"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2 py-1 rounded-lg transition-colors ${
                    isActive ? 'bg-blue-50' : 'hover:bg-slate-100'
                  }`
                }
              >
                <div className="text-right text-xs">
                  <p className="text-slate-900">{superAdmin.prenom} {superAdmin.nom}</p>
                  <p className="text-slate-400">{superAdmin.email}</p>
                </div>
                {resolveUploadUrl(superAdmin.photo_url) ? (
                  <img
                    src={resolveUploadUrl(superAdmin.photo_url)}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover border border-slate-200"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold bg-blue-100 text-blue-700">
                    {initiales(superAdmin.prenom, superAdmin.nom)}
                  </div>
                )}
              </NavLink>
            )}
            <button
              onClick={handleLogout}
              title="Déconnexion"
              className="p-2 text-slate-500 hover:bg-slate-100 hover:text-red-600 rounded-lg transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white py-4">
        <FooterMentions variant="light" />
      </footer>
    </div>
  );
}
